<script setup lang="ts">
/**
 * Saisir ou corriger le numéro d'un membre (geste 34, route 8 ;
 * `dialogueSaisirNumero` S:6910-6922, D127).
 *
 * Ce que l'utilisateur doit vivre : le propriétaire ou l'administrateur
 * saisit le numéro de mobile d'un membre qui n'en a pas, ou dont le numéro
 * n'est pas au format attendu. Le serveur le met au format international et
 * le vérifie : un numéro qu'il ne sait pas lire, ou qui ne reçoit pas de SMS,
 * est refusé par une phrase affichée telle quelle, et rien n'est écrit. Puis
 * la liste des membres est relue : l'état du numéro affiché est celui
 * enregistré.
 *
 * Le numéro saisi touche une vraie personne : il ne vit que dans ce
 * composant, n'est jamais écrit dans la console ni dans un store, et le
 * champ est vidé à la fermeture du dialogue.
 */
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import {
    cibleNumero,
    useActionsStore,
} from "@modules/alarmes-v2/stores/actions.store"
import { useMembresStore } from "@modules/alarmes-v2/stores/membres.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import type { EtatNumero } from "@modules/alarmes-v2/utils/prevenus"
import { phraseDuRefus } from "@modules/alarmes-v2/utils/ecritures"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** Le membre. */
    idUser: string
    /** Son nom, tel que l'écran le dit. */
    nom: string
    /** L'état de son numéro. */
    etatNumero: EtatNumero
}>()

const actions = useActionsStore()
const membres = useMembresStore()
const { acces } = useAcces()

const ouvert = ref(false)
/** La saisie : jamais ailleurs que dans ce champ. */
const saisie = ref("")

// ================================
// COMPUTED
// ================================
const getCible = computed(() => cibleNumero(props.idUser))
const getEtat = computed(() => actions.etat(getCible.value))
const getOccupee = computed(() => actions.occupee(getCible.value))

/** Le geste n'a de sens que sans numéro, ou pour un numéro à corriger. */
const getAProposer = computed(
    () =>
        props.etatNumero === "sans_numero" || props.etatNumero === "a_corriger"
)

const getAcces = computed(() => acces("saisir_numero_membre"))

const getRefus = computed((): string => {
    const e = getEtat.value
    if (!e || e.statut !== "refus" || !e.erreur) return ""
    const p = phraseDuRefus(e.erreur)
    return p.texte ?? t(p.cle as string)
})

const getTexteFait = computed(() =>
    getEtat.value?.statut === "fait"
        ? t("alarmesV2.actions.fait.numero", { nom: props.nom })
        : ""
)

// ================================
// HANDLERS
// ================================
/** Relit les membres : l'état du numéro affiché devient celui enregistré. */
async function relireLesMembres(): Promise<boolean> {
    return membres.relireApresEcriture()
}

function onOuvrir(): void {
    if (getOccupee.value) return
    actions.oublier(getCible.value)
    saisie.value = ""
    ouvert.value = true
}

function onFermer(): void {
    if (getOccupee.value) return
    saisie.value = ""
    ouvert.value = false
}

/** Envoie : UNE requête, jamais un numéro vide, puis la relecture. */
async function onEnregistrer(): Promise<void> {
    const numero = saisie.value.trim()
    if (getOccupee.value || numero === "") return
    const idUser = props.idUser
    const issue = await actions.ecrire({
        cible: cibleNumero(idUser),
        geste: "numero",
        // Rien de la réponse n'est gardé : l'état du numéro se relit (route 9).
        appel: () =>
            serviceAlarmesV2
                .alarmesV2CorrigerNumero(idUser, numero)
                .then(() => null),
        relire: relireLesMembres,
    })
    // Un refus garde le dialogue ouvert : la personne corrige sa saisie.
    if (issue.issue === "refus" || issue.issue === "ignoree") return
    saisie.value = ""
    ouvert.value = false
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <span
        v-if="(getAProposer && getAcces.affichage !== 'absent') || getEtat"
        class="mt-1 flex flex-col items-start gap-1"
        data-test="numero-membre"
    >
        <BoutonGeste
            v-if="getAProposer"
            :acces="getAcces"
            :libelle="
                etatNumero === 'a_corriger'
                    ? t('alarmesV2.actions.numero.corriger')
                    : t('alarmesV2.actions.numero.saisir')
            "
            classe="rounded border border-surface-300 px-2 py-1 text-xs font-medium text-surface-800"
            @click="onOuvrir"
        />
        <AnnonceEcriture
            v-if="getEtat && !ouvert"
            :cible="getCible"
            :texte-fait="getTexteFait"
            :texte-incertain="t('alarmesV2.actions.numero.incertain')"
            :relire="relireLesMembres"
        />

        <CadreDialogue
            v-if="ouvert"
            :titre="t('alarmesV2.actions.numero.titre', { nom })"
            :bloque="getOccupee"
            @fermer="onFermer"
        >
            <template #intro>
                <p class="text-sm text-surface-600">
                    {{ t("alarmesV2.actions.numero.texte") }}
                </p>
            </template>
            <label class="flex flex-col gap-1 text-sm">
                {{ t("alarmesV2.actions.numero.libelle") }}
                <input
                    v-model="saisie"
                    type="tel"
                    autocomplete="off"
                    maxlength="40"
                    class="rounded border border-surface-300 px-2 py-1"
                    :placeholder="t('alarmesV2.actions.numero.exemple')"
                    :aria-invalid="getRefus ? 'true' : undefined"
                    data-test="numero-saisie"
                />
            </label>
            <p class="text-xs text-surface-600">
                {{ t("alarmesV2.actions.numero.aide") }}
            </p>
            <p v-if="getOccupee" class="text-sm text-surface-700" role="status">
                {{ t("alarmesV2.actions.envoi") }}
            </p>
            <p
                v-else-if="getRefus"
                class="text-sm text-red-800"
                role="alert"
                data-test="refus"
            >
                {{ getRefus }}
            </p>
            <template #pied>
                <button
                    type="button"
                    class="rounded border border-surface-300 px-3 py-1.5 text-sm"
                    :aria-disabled="getOccupee ? 'true' : undefined"
                    data-focus-initial
                    data-test="annuler"
                    @click="onFermer"
                >
                    {{ t("alarmesV2.actions.annuler") }}
                </button>
                <button
                    type="button"
                    class="rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                    :aria-disabled="
                        getOccupee || saisie.trim() === '' ? 'true' : undefined
                    "
                    data-test="enregistrer"
                    @click="onEnregistrer"
                >
                    {{ t("alarmesV2.actions.enregistrer") }}
                </button>
            </template>
        </CadreDialogue>
    </span>
</template>
